/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-overview-panels-dialog/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from '@progress/kendo-data-query';
import React, { useEffect, useState } from 'react';
import LibraryGrid from '@components/admin/library-grid';
import { StudyOverviewPanel } from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/panels-overview-grid/panels-overview-grid.interface';
import Button from '@components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from '@components/core/dialog';
import { useBoolean, useTranslation } from '@hooks/index';

const AddOverviewPanelsDialog = ({
    title,
    onClose,
    onAssign,
    items,
    currentItems,
}: {
    title: string;
    onClose: () => void;
    onAssign: (closeModal: boolean, selectedItems: StudyOverviewPanel[]) => void;
    items: StudyOverviewPanel[];
    currentItems: StudyOverviewPanel[];
}) => {
    const { t } = useTranslation();
    const [selectedItems, setSelectedItems] = useState<StudyOverviewPanel[]>([]);
    const [filteredPanels, setFilteredPanels] = useState<StudyOverviewPanel[]>([]);
    const isButtonsDisabled = useBoolean(false);
    const [topScroll, setTopScroll] = useState<number>(0); // Incorrect filter menu position fix

    useEffect(() => {
        // Incorrect filter menu position fix
        setTopScroll(document.documentElement.scrollTop);
        document.documentElement.scrollTop = 0;
    }, []);

    useEffect(() => {
        const notAddedItems = items.filter(
            (item: StudyOverviewPanel) =>
                !currentItems.find((curItem: StudyOverviewPanel) => item.panelId === curItem.panelId) && item.active,
        );
        setFilteredPanels(
            orderBy(notAddedItems, [{ field: 'plotTitle', dir: 'asc' }])?.map((item) => ({
                ...item,
            })),
        );
    }, [items, currentItems]);

    const close = () => {
        document.documentElement.scrollTop = topScroll;
        setSelectedItems([]);
        onClose();
        isButtonsDisabled.setFalse();
    };

    const addItems = (closeModal?: boolean) => {
        isButtonsDisabled.setTrue();
        onAssign(closeModal, selectedItems);
        setSelectedItems([]);
    };

    const gridColumns = [
        {
            title: t('admin:plotTitle'),
            field: 'plotTitle',
            id: 'study-row-overview-plot-title',
        },
        {
            title: t('admin:model'),
            field: 'model',
            id: 'stury-row-overview-model',
        },
    ];

    return (
        <CustomDialog onClose={close} scroll="paper" maxWidth="md" open={open}>
            <DialogTitle id="add-overview-panels-title">
                {title}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-overview-panels-content pb-0 mb-20">
                <LibraryGrid
                    items={filteredPanels}
                    columns={gridColumns}
                    itemsType={t('admin:panelRecordS')}
                    idFieldName="panelId"
                    selection={{
                        selectedItems,
                        setSelectedItems: (selItems: any) => {
                            isButtonsDisabled.setFalse();
                            setSelectedItems(selItems);
                        },
                        showItemsCount: true,
                    }}
                />
            </DialogContent>
            <DialogActions className="actions-add-risk assign-items flex-wrap pt-0">
                <Button
                    text="add"
                    id="add-library-risk-save"
                    disabled={!selectedItems.length || isButtonsDisabled.value}
                    onClick={() => addItems(false)}
                />
                <Button
                    text="addAndClose"
                    id="add-library-risk-save-close"
                    disabled={!selectedItems.length || isButtonsDisabled.value}
                    onClick={() => addItems(true)}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AddOverviewPanelsDialog;
